import Pusher from 'pusher-js'
import { onUnmounted, watch } from 'vue'
import { useBatchStore } from '@/stores/BatchStore'
import { useConnectionStore } from '@/stores/ConnectionStore'
import { useListingsStore } from '@/stores/ListingsStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useImportsStore } from '@/stores/ImportsStore'
import { useCardStore } from '@/stores/CardStore'
import { useRoute, useRouter } from 'vue-router'
import { PUSHER_EVENTS_TO_CHANNELS, DEFAULT_TOAST_NOTIFICATION } from '@/constants'
import { isMobile } from '@/helpers/general'
import { useInventoryStore } from '@/stores/InventoryStore'

export function usePusher(userStore) {
  const batchStore = useBatchStore()
  const connectionStore = useConnectionStore()
  const globalStore = useGlobalStore()
  const inventoryStore = useInventoryStore()
  const configStore = useConfigStore()
  const cardStore = useCardStore()
  const importsStore = useImportsStore()
  const listingsStore = useListingsStore()

  const route = useRoute()
  const router = useRouter()

  let pusher = null
  let channel = null

  const initializePusher = () => {
    Pusher.logToConsole = true

    pusher = new Pusher('f1107eef8ca40861ee35', {
      cluster: 'mt1'
    })

    channel = pusher.subscribe(`channel-${userStore.user?.id}`)

    channel.bind('batchUpdate', handleBatchUpdate)
    channel.bind('collx_listing_completed', (data, event) => handleImportCompleted(data, event, 'collx_listing_completed'))
    channel.bind('collx_import_completed', (data, event) => handleImportCompleted(data, event, 'collx_import_completed'))
    channel.bind('ebay_import_completed', (data, event) => handleImportCompleted(data, event, 'ebay_import_completed'))
    channel.bind('shopify_import_completed', (data, event) => handleImportCompleted(data, event, 'shopify_import_completed'))
    channel.bind('whatnot_import_completed', (data, event) => handleImportCompleted(data, event, 'whatnot_import_completed'))
    // channel.bind('ebay_import_failed', (data, event) => handleImportCompleted(data, event, 'ebay_import_failed'))
    channel.bind('ebay_import_failed', (data, event) => console.log('>>>--', data, event, 'ebay_import_failed'))
    channel.bind('otp_login_completed', handleOtpLoginCompleted)
    channel.bind('selected_image_side', handleSelectedImageSide)
    channel.bind('otp_login_ended', handleOtpLoginEnded)
    channel.bind('collx_auth_failed', handleCollxAuthFailed)

    channel.bind('ebay_listings_import_started', (data) => handleImportInventory('ebay', data))
    channel.bind('ebay_listings_import_completed', (data) => handleImportInventory('ebay', data))
    channel.bind('ebay_listings_import_progress', (data) => handleImportInventory('ebay', data))

    channel.bind('collx_listings_import_started', (data) => handleImportInventory('collx', data))
    channel.bind('collx_listings_import_completed', (data) => handleImportInventory('collx', data))
    channel.bind('collx_listings_import_progress', (data) => handleImportInventory('collx', data))

    channel.bind('whatnot_listings_import_started', (data) => handleImportInventory('whatnot', data))
    channel.bind('whatnot_listings_import_completed', (data) => handleImportInventory('whatnot', data))
    channel.bind('whatnot_listings_import_progress', (data) => handleImportInventory('whatnot', data))

    channel.bind('job_updated', (data) => handleJobProgress(data))

    channel.bind('subscription_cancelled', (data) => handleSubscriptionCancelled(data))

    channel.bind('listings_ended', (data) => handleListingsEnded(data))
  }

  const showListingsEndedToast = () => {
    listingsStore.additionalInfoAlreadyFetched = false
    listingsStore.fetchListingsAdditionalInfo()

    cardStore.selectedCardData = { ...cardStore.selectedCardData, listingStatus: 'ended' }

    globalStore.toastNotification = {
      show: true,
      message: `Your listing has ended.`,
      type: 'success',
    }

    globalStore.toastTimeoutId = setTimeout(() => {
      globalStore.toastNotification = { ...DEFAULT_TOAST_NOTIFICATION }
      globalStore.toastTimeoutId = null
    }, 5000)
  }

  const handleListingsEnded = (data) => {
    if (globalStore.toastTimeoutId) {
      // First toast is still visible — let it finish its remaining time, then show second
      const elapsed = Date.now() - globalStore.toastStartTime
      const remaining = Math.max(0, 5000 - elapsed)

      clearTimeout(globalStore.toastTimeoutId)
      globalStore.toastTimeoutId = setTimeout(() => {
        globalStore.toastNotification = { ...DEFAULT_TOAST_NOTIFICATION }
        globalStore.toastStartTime = null
        showListingsEndedToast()
      }, remaining)
    } else {
      showListingsEndedToast()
    }
  }

  const handleJobProgress = (data) => {
    userStore.updateUserField('job_info', data.user?.job_info)

    console.log('job info updated -- job_updated', data)
  }

  const handleSubscriptionCancelled = (data) => {
    configStore.fetchConfigs()
  }

  const handleImportInventory = (key, data) => {
    typeof userStore.user.import_progress === 'string' && (userStore.user.import_progress = JSON.parse(userStore.user.import_progress))
    userStore.user.import_progress[key] = data

    if ((!data.active || data.progress === 100) && !data.noItems) {
      inventoryStore.fetchInventory({ id: userStore.user.default_inventory_id, perPage: 25, isPagination: false })

      importsStore.showImportDialog = true
      importsStore.importDialogData.confirmLabel = `Import Completed`
      importsStore.importDialogData.confirmationMessage = data.totalItems ? `${data.totalItems} items have been added or updated in your inventory.` : 'Items have been added or updated in your inventory.' // TODO: need to fix it why we have noItems: false and totalItems: 0
      importsStore.importDialogData.singleOkButton = true
      
    }

    if (data.noItems && !data.active) {
      importsStore.showImportDialog = true
      importsStore.importDialogData.confirmLabel = `Import Completed`
      importsStore.importDialogData.confirmationMessage = 'No items were available to import'
      importsStore.importDialogData.singleOkButton = true
    }

    // TODO: should be removed after fix event in backend
    setTimeout(() => {
      userStore.fetchUser()
    }, 2000)
  }

  const handleBatchUpdate = (data) => {
    if (
      data?.Event === 'card_processing_completed' ||
      data?.Event === 'card_processing_failed' ||
      data?.Event === 'rotate_processing_completed'
    ) {
      if (route.name === 'InventoryDetails') {
        inventoryStore.updatedInventoryCard(data.CardId, true)
        globalStore.pusherLoading = false
      } else {  
        cardStore.fetchUpdatedCard(data.CardId)
        console.log(`>>> ${data.Event} <<<`, data)
      }
    }

    if ( data?.Event === 'llm_search_started') {
      const cardStore = useCardStore()

      const card = cardStore.cards.find(item => item.id === data?.CardId)

      if (card) {
        card.runProcessingAnimation = true

        console.log('llm_search_started update card', card)
        Object.assign(card, card)
      }
    }

    if (data?.Event === 'replace_card_processing_completed') {
      if (route.name === 'InventoryDetails') {
        inventoryStore.updatedInventoryCard(data.CardId, true)
      } else {  
        cardStore.fetchUpdatedCard(data.CardId)
      }

      setTimeout(() => {
        batchStore.replaceImageLoader = false
        globalStore.pusherLoading = false
      }, 0)
    }

    if (
      (data?.Event === 'remove_banner_completed' || data?.Event === 'add_banner_completed') &&
      data.LastCard
    ) {
      cardStore.fetchSelectedCards(null, false)
    }

    if (data?.Event === 'swap_processing_completed') {
      if (route.name === 'InventoryDetails') {
        inventoryStore.updatedInventoryCard(data.CardId, true)
        globalStore.pusherLoading = false
      } else {  
        cardStore.fetchUpdatedCard(data.CardId)
      }

      if (data.LastCard) {
        cardStore.updateSwappedToast(true, 'Image swap complete!')
      }
    }

    if (data?.Event === 'card_reprocessing_completed' && data.LastCard) {
      cardStore.fetchSelectedCards(null, false)
    }

    if (data?.Event === 'scan_counts_updated') {
      configStore.config = {
        ...configStore.config,
        availableScans: data.AvailableScans,
        totalScans: data.TotalScans
      }
    }

    if (data?.Event === 'card_uploaded_to_cloud_storage' && data?.MobileUpload) {
      const filesNames = data?.UploadFilenames && JSON.parse(data?.UploadFilenames)

      batchStore.uploads = [
        ...batchStore.uploads,
        {
          front: { name: filesNames.frontFilename || '' },
          back: { name: filesNames.backFilename || '' },
          thumbnail: null,
          thumbnailFront: data.Images.FrontSmImage || null,
          thumbnailBack: data.Images.BackSmImage || null,
          progress: 0,
          pending: false,
          uploaded: true,
          cardId: data.CardId
        }
      ]
      batchStore.countOfUploadedImagesFromMobile =
        batchStore.countOfUploadedImagesFromMobile + 1
    }
  }

  const handleImportCompleted = (data, event, typeOfEvent) => {
    console.log('import completed ---', typeOfEvent, data)
    const cardStore = useCardStore()

    if (typeOfEvent !== 'collx_listing_completed') {
      connectionStore.showExportToast = true
      if (typeOfEvent === 'ebay_import_failed') {
        globalStore.errors = true
      }

      connectionStore.exportedChannelInfo = {
        ...PUSHER_EVENTS_TO_CHANNELS[typeOfEvent].channel,
        label: 'Listings published',
        message: `cards have been listed on ${PUSHER_EVENTS_TO_CHANNELS[typeOfEvent].name}.`,
        key: PUSHER_EVENTS_TO_CHANNELS[typeOfEvent].channel?.key === 'ebay' ? 'ebay-direct' : PUSHER_EVENTS_TO_CHANNELS[typeOfEvent].channel?.key,
        countOfCards: data.successful_cards_data?.length,
        ...data
      }

      let parsedErrors = []
      
      try {
        if (data.failed_cards_data) {
          parsedErrors = JSON.parse(data.failed_cards_data)
        }
      } catch {
        parsedErrors = []
      }
      
      connectionStore.exportedErrorsInfo = {
        errors: [...parsedErrors],
        channel: PUSHER_EVENTS_TO_CHANNELS[typeOfEvent].channel,
        name: PUSHER_EVENTS_TO_CHANNELS[typeOfEvent].name
      }
    }
    

    if (data.successful_cards_data && data.successful_cards_data.length && typeOfEvent !== 'collx_import_completed') {
      // Update cards in cardStore based on successful_cards_data
      cardStore.updateCardsFromSuccessfulData(data.successful_cards_data)
    }
    
    // if (route.params.batchId) {
    //   cardStore.fetchSelectedCards(null, false)
    // }
  }

  const handleOtpLoginCompleted = () => {
    if (!isMobile()) {
      // Use the batchId captured at QR generation time — route.params.batchId is unreliable
      // here because the user may have navigated away from the batch page before this event fires.
      const batchId = batchStore.otpBatchId || route.params.batchId
      if (!batchId) return
      router.push(`/batches/${batchId}/add/upload`)
      userStore.otpLoginDone = true
    }
  }

  const handleSelectedImageSide = (data) => {
    if (!isMobile()) {
      router.replace(`${route.path}?sides=${data.side}`)
    }
  }

  const handleOtpLoginEnded = () => {
    if (isMobile()) {
      router.push(`/mobile-scan/session-ended`)
    }
  }

  const handleCollxAuthFailed = () => {
    connectionStore.collxAuthFailedFunc()
  
    router.push(`/connections/collx/account-connection`)
  }

  watch(() => userStore.loggedIn, () => {
    if (userStore.loggedIn) {
      initializePusher()
    }
  })

  onUnmounted(() => {
    if (channel) {
      channel.unbind_all()
      channel.unsubscribe()
    }
    if (pusher) {
      pusher.disconnect()
    }
  })
}
